import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatRelativeDate as formatRelativeDateUtil } from '../utils/relativeDate';
import { useNavigate } from 'react-router-dom';
import { dashboardService, workflowService, tagService, DashboardFilters } from '../services';
import { Dashboard, Workflow, Tag } from '../types';
import { ALL_WIDGET_DEFS, getWidgetLabel } from '../components/widgets/registry';
import { useThrottle } from '../hooks';
import { getErrorMessage } from '../utils/errors';
import ImportModal from '../components/common/ImportModal';
import TagBadge from '../components/common/TagBadge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import DescriptionCells from '../components/common/ExpandableDescription';
import Select from '../components/ui/Select';
import LibraryListPage from '../components/ui/LibraryListPage';
import SortHeader from '../components/ui/SortHeader';

const WidgetIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const WorkflowIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const WIDGET_TYPE_OPTIONS = ALL_WIDGET_DEFS
  .filter(d => d.meta.isCreatable)
  .map(d => ({ value: d.type, label: getWidgetLabel(d.type) }));

// Helper to count how many workflows use a dashboard
const countWorkflowsUsingDashboard = (dashboardId: string, workflows: Workflow[]): number => {
  let count = 0;
  for (const workflow of workflows) {
    const nodes = workflow.flowData?.nodes || [];
    for (const node of nodes) {
      if ((node.data as Record<string, unknown>)?.dashboardId === dashboardId) {
        count++;
        break; // Count each workflow only once
      }
    }
  }
  return count;
};

const Dashboards: React.FC = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [selectedWidgetType, setSelectedWidgetType] = useState<string>('');
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDescId, setExpandedDescId] = useState<string | null>(null);
  const throttledSearch = useThrottle(searchQuery);

  // Sort state
  type DashSortField = 'name' | 'widgets' | 'workflows' | 'tags' | 'updated';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<DashSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: DashSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'updated' ? 'desc' : 'asc');
    }
  };

  // Load workflows + tag pool for the filter dropdowns
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const response = await workflowService.getWorkflows(1, 100);
        setWorkflows(response.data);
      } catch (err) {
        console.error('Failed to load workflows:', err);
      }
    };
    const loadTags = async () => {
      try {
        const data = await tagService.list();
        setTags(data);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadWorkflows();
    loadTags();
  }, []);

  const loadDashboards = useCallback(async () => {
    try {
      setLoading(true);
      const filters: DashboardFilters = {};
      if (throttledSearch) filters.search = throttledSearch;
      if (selectedWorkflow) filters.workflowId = selectedWorkflow;
      if (selectedWidgetType) filters.widgetType = selectedWidgetType;
      if (selectedTagId) filters.tagId = selectedTagId;

      const response = await dashboardService.getDashboards(page, 12, filters);
      setDashboards(response.data);
      setTotalPages(Math.ceil(response.total / response.pageSize));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load dashboards'));
    } finally {
      setLoading(false);
    }
  }, [page, throttledSearch, selectedWorkflow, selectedWidgetType, selectedTagId]);

  useEffect(() => {
    loadDashboards();
  }, [loadDashboards]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [throttledSearch, selectedWorkflow, selectedWidgetType, selectedTagId]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedWorkflow('');
    setSelectedWidgetType('');
    setSelectedTagId(undefined);
  };

  const hasActiveFilters = !!(
    searchQuery || selectedWorkflow || selectedWidgetType || selectedTagId
  );

  const handleCreate = () => navigate('/dashboard-builder');
  const handleEdit = (id: string) => navigate(`/dashboard-builder/${id}`);
  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    loadDashboards();
  };

  const formatRelativeDate = (dateString: string) =>
    formatRelativeDateUtil(dateString, { localeFormat: { month: 'short', day: 'numeric' } });

  const filterCount =
    [selectedWorkflow, selectedWidgetType, selectedTagId].filter(Boolean).length +
    (searchQuery ? 1 : 0);

  // Sort dashboards
  const sortedDashboards = useMemo(() => {
    const sorted = [...dashboards];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'widgets':
          cmp = a.widgets.length - b.widgets.length;
          break;
        case 'workflows':
          cmp = countWorkflowsUsingDashboard(a.id, workflows) - countWorkflowsUsingDashboard(b.id, workflows);
          break;
        case 'tags':
          cmp = (a.tags?.length ?? 0) - (b.tags?.length ?? 0);
          break;
        case 'updated':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [dashboards, sortField, sortDir, workflows]);

  return (
    <>
      <LibraryListPage
        title="Dashboard Library"
        description="Compose widgets into dashboards for workflow steps"
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search dashboards..."
        tags={tags}
        selectedTagId={selectedTagId}
        onTagChange={setSelectedTagId}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filterCount={filterCount}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearFilters}
        filterPanel={
          <>
            <Select
              value={selectedWorkflow}
              onChange={e => setSelectedWorkflow(e.target.value)}
              options={workflows.map(w => ({ value: w.id, label: w.name }))}
              placeholder="All workflows"
              label="In Workflow"
              className="flex-1 min-w-[200px] max-w-xs"
            />
            <Select
              value={selectedWidgetType}
              onChange={e => setSelectedWidgetType(e.target.value)}
              options={WIDGET_TYPE_OPTIONS}
              placeholder="All widget types"
              label="Contains Widget Type"
              className="flex-1 min-w-[200px] max-w-xs"
            />
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setIsImportModalOpen(true)}>
              Import
            </Button>
            {dashboards.length > 0 && (
              <Button variant="primary" size="sm" onClick={handleCreate}>
                Create Dashboard
              </Button>
            )}
          </>
        }
        loading={loading}
        error={error}
        onDismissError={() => setError(null)}
        isEmpty={dashboards.length === 0}
        emptyTitle={hasActiveFilters ? 'No matching dashboards' : 'No dashboards yet'}
        emptyDescription={
          hasActiveFilters
            ? "Try adjusting your search or filters to find what you're looking for"
            : 'Get started by creating a dashboard to organize your widgets'
        }
        emptyAction={
          hasActiveFilters
            ? { label: 'Clear filters', onClick: clearFilters }
            : { label: 'Create Dashboard', onClick: handleCreate }
        }
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      >
        <Card variant="default">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50 border-y border-neutral-100">
                  <SortHeader label="Dashboard" active={sortField === 'name'} dir={sortDir} onSort={() => toggleSort('name')} />
                  <SortHeader label="Widgets" active={sortField === 'widgets'} dir={sortDir} onSort={() => toggleSort('widgets')} />
                  <SortHeader label="Workflows" active={sortField === 'workflows'} dir={sortDir} onSort={() => toggleSort('workflows')} />
                  <SortHeader label="Tags" active={sortField === 'tags'} dir={sortDir} onSort={() => toggleSort('tags')} className="hidden md:table-cell" />
                  <SortHeader label="Updated" active={sortField === 'updated'} dir={sortDir} onSort={() => toggleSort('updated')} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
                  <th className="px-6 py-3 hidden lg:table-cell w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedDashboards.map(dashboard => {
                  const workflowCount = countWorkflowsUsingDashboard(dashboard.id, workflows);
                  return (
                    <React.Fragment key={dashboard.id}>
                    <tr
                      className="hover:bg-neutral-50 transition-colors cursor-pointer group"
                      onClick={() => handleEdit(dashboard.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-neutral-900 group-hover:text-primary-700 transition-colors">
                          {dashboard.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                          <WidgetIcon />
                          {dashboard.widgets.length}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                          <WorkflowIcon />
                          {workflowCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        {dashboard.tags && dashboard.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {dashboard.tags.slice(0, 3).map(tag => (
                              <TagBadge key={tag.id} tag={tag} />
                            ))}
                            {dashboard.tags.length > 3 && (
                              <Badge size="sm" variant="default">+{dashboard.tags.length - 3}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-neutral-400">{formatRelativeDate(dashboard.updatedAt)}</span>
                      </td>
                      <DescriptionCells
                        text={dashboard.description}
                        expanded={expandedDescId === dashboard.id}
                        onToggle={(e) => { e.stopPropagation(); setExpandedDescId(expandedDescId === dashboard.id ? null : dashboard.id); }}
                      />
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </LibraryListPage>

      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        allowedTypes={['dashboard']}
      />
    </>
  );
};

export default Dashboards;
